import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { DealFieldSection, DealFieldSectionsPayload, PloomesDealDetail } from '../types';
import DetailCard from './DetailCard';
import { FieldSectionCard } from './PloomesDealFieldSectionsPanel';

type Props = {
    deal: PloomesDealDetail;
};

type InfoRow = {
    label: string;
    value: string | null | undefined;
};

function InfoGrid({ rows }: { rows: InfoRow[] }) {
    return (
        <dl className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(max(12.5rem,25%),1fr))]">
            {rows.map((row) => {
                const empty = row.value == null || String(row.value).trim() === '';
                return (
                    <div
                        key={row.label}
                        className="flex min-w-0 flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-1.5 dark:border-zinc-800 dark:bg-zinc-950/40"
                    >
                        <dt className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-500">
                            {row.label}
                        </dt>
                        <dd
                            className={
                                empty
                                    ? 'text-sm text-gray-400 dark:text-zinc-600'
                                    : 'line-clamp-3 break-words text-sm font-medium leading-snug text-gray-900 dark:text-zinc-100'
                            }
                            title={empty ? undefined : String(row.value)}
                        >
                            {empty ? '—' : row.value}
                        </dd>
                    </div>
                );
            })}
        </dl>
    );
}

function fullAddress(deal: PloomesDealDetail): string | null {
    const parts = [
        deal.contact_address,
        deal.contact_neighborhood,
        deal.contact_city,
        deal.contact_state,
        deal.contact_zip_code_formatted,
    ].filter((p) => p && String(p).trim() !== '');

    return parts.length > 0 ? parts.join(' · ') : null;
}

export default function PloomesDealClientPanel({ deal }: Props) {
    const { t } = useI18n();

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ploomes-deal-field-sections', deal.id],
        queryFn: async () => {
            const { data: payload } = await api.get<{ data: DealFieldSectionsPayload }>(
                `/ploomes-deals/${deal.id}/field-sections`,
            );
            return payload.data;
        },
        staleTime: 60_000,
    });

    const cadastroRows: InfoRow[] = [
        { label: t('v2.ploomes_deals.client_tab.name'), value: deal.contact_name },
        { label: t('v2.ploomes_deals.client_tab.legal_name'), value: deal.contact_legal_name },
        { label: t('v2.ploomes_deals.client_tab.register'), value: deal.contact_register_formatted },
        { label: t('v2.ploomes_deals.client_tab.email'), value: deal.contact_email },
        { label: t('v2.ploomes_deals.client_tab.phone'), value: deal.contact_phone_formatted },
        { label: t('v2.ploomes_deals.client_tab.cnae'), value: deal.contact_cnae },
        { label: t('v2.ploomes_deals.client_tab.address'), value: fullAddress(deal) },
        { label: t('v2.ploomes_deals.client_tab.segment'), value: deal.segment },
        {
            label: t('v2.ploomes_deals.client_tab.classification'),
            value: deal.client_classification,
        },
        {
            label: t('v2.ploomes_deals.client_tab.buckler_one'),
            value: deal.is_buckler_one_client
                ? deal.buckler_one_franchisor_name || t('v2.ploomes_deals.badges.buckler_one')
                : t('common.no'),
        },
        { label: t('v2.ploomes_deals.client_tab.notes'), value: deal.contact_note },
    ];

    const clientSections: DealFieldSection[] = (data?.sections ?? []).filter(
        (section) => section.key === 'client' || section.key === 'matrix',
    );

    // Campos contact_* que caíram em outras seções (exceto client/matrix já listadas).
    const extraContactFields = (data?.sections ?? [])
        .filter((section) => section.key !== 'client' && section.key !== 'matrix')
        .flatMap((section) => section.fields)
        .filter((field) => typeof field.field_key === 'string' && field.field_key.startsWith('contact_'));

    const extraContactSection: DealFieldSection | null =
        extraContactFields.length > 0
            ? {
                  key: 'contact_extra',
                  title_key: 'v2.ploomes_deals.client_tab.other_contact_fields',
                  fields: extraContactFields,
              }
            : null;

    return (
        <div className="space-y-3">
            <DetailCard title={t('v2.ploomes_deals.client_tab.cadastro')} className="shadow-none [&>div:last-child]:p-3">
                <InfoGrid rows={cadastroRows} />
                {deal.contact_id ? (
                    <p className="mt-3 text-[11px] text-gray-500 dark:text-zinc-500">
                        {t('v2.ploomes_deals.client_tab.ploomes_contact_id', { id: String(deal.contact_id) })}
                    </p>
                ) : null}
            </DetailCard>

            {isLoading && <LoadingState variant="cards" />}

            {isError && (
                <p className="py-2 text-sm text-gray-500 dark:text-zinc-400">
                    {t('v2.ploomes_deals.client_tab.fields_load_error')}
                </p>
            )}

            {!isLoading &&
                !isError &&
                [...clientSections, ...(extraContactSection ? [extraContactSection] : [])].map((section) => (
                    <FieldSectionCard key={section.key} section={section} />
                ))}
        </div>
    );
}
